import { InputHTMLAttributes, ReactNode, forwardRef, useId } from "react";

/**
 * Input — Archival Studio primitive.
 *
 * Pairs a mono uppercase eyebrow label with a paper-raised input.
 * Rounded hairline input with a walnut focus ring.
 *
 * Source: project/preview/components-form.html
 */

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Mono uppercase eyebrow label above the field (sentence case in copy). */
  label?: ReactNode;
  /** Optional helper text below the field. */
  helper?: ReactNode;
  /** Render the value in mono (timestamps, dateline, hash digests). */
  mono?: boolean;
}

const BASE =
  "min-w-0 w-full font-ui text-t-14 text-ink-1 bg-paper-raised/72 " +
  "border border-rule-faint rounded-as-2 " +
  "min-h-11 " +
  "px-3 py-2 outline-none " +
  "transition-[background-color,border-color,box-shadow] duration-as-1 ease-as-out " +
  "placeholder:text-ink-4 " +
  "focus:border-walnut focus:bg-paper-raised focus:shadow-[0_0_0_3px_rgb(var(--as-walnut-rgb)/0.18)] " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const MONO = "font-mono text-t-13";

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helper, mono = false, className = "", id: idProp, ...rest },
  ref,
) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <label htmlFor={id} className="flex flex-col gap-[4px]">
      {label && (
        <span className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3">
          {label}
        </span>
      )}
      <input
        ref={ref}
        id={id}
        className={`${BASE} ${mono ? MONO : ""} ${className}`}
        {...rest}
      />
      {helper && <span className="font-ui text-t-13 text-ink-3">{helper}</span>}
    </label>
  );
});
